import React, { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Card, Typography, Row, Col, Spin, Statistic, message, Result, Empty } from 'antd';
import { BookOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import ExamManage from './components/Manage/ExamManage';
import styles from './index.module.css';
import axios from '../../utils/axios';

const { Title, Paragraph } = Typography;

const ExamSystem = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // 只在需要显示分类列表时才获取数据
    if (location.pathname === '/exam/manage' || location.pathname === '/exam/practice') {
      fetchCategories();
    } else {
      // 在主页时重置加载状态
      setLoading(false);
    }
  }, [location.pathname]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      console.log('Fetching categories...');
      const response = await axios.get('/api/exam/economist_exam');
      console.log('API Response:', response);
      
      if (response.data.success && response.data.data?.questions) {
        const questions = response.data.data.questions;
        console.log('Questions data:', questions);
        
        // 按照 exam_data 分类统计题目
        const categoriesMap = questions.reduce((acc, question) => {
          // 确保 exam_data 和 category 都存在且有值
          const examData = question.exam_data || '未分类';
          const category = question.category || '工商管理';
          
          if (!acc[examData]) {
            acc[examData] = {
              name: examData,
              count: 0,
              completedCount: 0,
              category: category
            };
          }
          acc[examData].count++;
          return acc;
        }, {});

        const categoriesList = Object.values(categoriesMap);
        console.log('Processed Categories:', categoriesList);
        setCategories(categoriesList);
      } else {
        console.log('Invalid response format:', response);
        message.error('获取分类数据失败');
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      message.error('加载分类失败');
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (category) => {
    // 传递 exam_data 和 category 两个参数
    navigate(`/exam/practice/${encodeURIComponent(category.category)}/${encodeURIComponent(category.name)}`);
  };

  // 修改管理页面的判断逻辑，添加错误处理
  if (location.pathname === '/exam/manage') {
    try {
      return <ExamManage />;
    } catch (error) {
      console.error('Error loading ExamManage:', error);
      return (
        <Result
          status="error"
          title="加载失败"
          subTitle="试题管理页面加载失败，请稍后重试"
        />
      );
    }
  }

  // 只有在 /exam 主页时显示介绍内容
  if (location.pathname === '/exam') {
    return (
      <div className={styles.examSystemContainer}>
        <Card className={styles.categoryCard}>
          <Title level={2} className={styles.pageTitle}>
            职称考试介绍
          </Title>
          
          <div className={styles.featuresSvg}>
            <svg width="1100" height="300" viewBox="0 0 1100 300" xmlns="http://www.w3.org/2000/svg">
              {/* 背景 */}
              <rect width="1100" height="300" fill="#1f1f1f" rx="20"/>
              
              {/* 1. 系统化的知识点梳理 */}
              <g transform="translate(50, 50)">
                <rect x="0" y="0" width="220" height="200" rx="15" fill="#262626" filter="drop-shadow(0 4px 6px rgba(0,0,0,0.2))"/>
                <g transform="translate(30, 30)">
                  <path d="M0 30 L60 30 M20 50 L100 50 M40 70 L80 70" 
                        stroke="#1ED6FF" stroke-width="3" stroke-linecap="round"/>
                  <circle cx="110" cy="30" r="5" fill="#1ED6FF"/>
                  <circle cx="110" cy="50" r="5" fill="#1ED6FF"/>
                  <circle cx="90" cy="70" r="5" fill="#1ED6FF"/>
                </g>
                <text x="30" y="140" fill="#ffffff" font-size="16" font-weight="bold">系统化知识梳理</text>
                <text x="30" y="165" fill="#999999" font-size="13">构建完整知识体系</text>
              </g>

              {/* 2. 大量真题练习 */}
              <g transform="translate(320, 50)">
                <rect x="0" y="0" width="220" height="200" rx="15" fill="#262626" filter="drop-shadow(0 4px 6px rgba(0,0,0,0.2))"/>
                <g transform="translate(30, 30)">
                  <rect x="10" y="0" width="120" height="80" fill="#262626" stroke="#ff6b6b" stroke-width="2"/>
                  <line x1="25" y1="20" x2="115" y2="20" stroke="#ff6b6b" stroke-width="2"/>
                  <line x1="25" y1="40" x2="95" y2="40" stroke="#ff6b6b" stroke-width="2"/>
                  <line x1="25" y1="60" x2="75" y2="60" stroke="#ff6b6b" stroke-width="2"/>
                  <circle cx="140" cy="10" r="15" fill="#ff6b6b"/>
                  <text x="133" y="15" fill="#262626" font-size="12">A+</text>
                </g>
                <text x="30" y="140" fill="#ffffff" font-size="16" font-weight="bold">大量真题练习</text>
                <text x="30" y="165" fill="#999999" font-size="13">提升实战能力</text>
              </g>

              {/* 3. 专业的答疑解析 */}
              <g transform="translate(590, 50)">
                <rect x="0" y="0" width="220" height="200" rx="15" fill="#262626" filter="drop-shadow(0 4px 6px rgba(0,0,0,0.2))"/>
                <g transform="translate(30, 30)">
                  <path d="M10 0 C40 0 80 0 110 0 C120 0 130 10 130 20 L130 40 C130 50 120 60 110 60 L40 60 L20 80 L20 60 C10 60 0 50 0 40 L0 20 C0 10 10 0 20 0" 
                        fill="#2ecc71"/>
                  <circle cx="45" cy="30" r="4" fill="#262626"/>
                  <circle cx="65" cy="30" r="4" fill="#262626"/>
                  <circle cx="85" cy="30" r="4" fill="#262626"/>
                </g>
                <text x="30" y="140" fill="#ffffff" font-size="16" font-weight="bold">专业答疑解析</text>
                <text x="30" y="165" fill="#999999" font-size="13">解决学习难点</text>
              </g>

              {/* 4. 个性化的学习计划 */}
              <g transform="translate(860, 50)">
                <rect x="0" y="0" width="220" height="200" rx="15" fill="#262626" filter="drop-shadow(0 4px 6px rgba(0,0,0,0.2))"/>
                <g transform="translate(30, 30)">
                  <rect x="10" y="0" width="120" height="80" fill="#262626" stroke="#9b59b6" stroke-width="2"/>
                  <rect x="10" y="0" width="120" height="20" fill="#9b59b6"/>
                  <path d="M25 35 L115 35 M25 55 L85 55" stroke="#9b59b6" stroke-width="2"/>
                  <circle cx="100" cy="55" r="8" fill="#9b59b6"/>
                  <path d="M97 55 L100 58 L105 53" stroke="#262626" stroke-width="2" fill="none"/>
                </g>
                <text x="30" y="140" fill="#ffffff" font-size="16" font-weight="bold">个性化学习计划</text>
                <text x="30" y="165" fill="#999999" font-size="13">定制专属路径</text>
              </g>

              {/* 连接装饰 */}
              <g stroke="#303030" stroke-width="2">
                <path d="M270 150 L320 150" stroke-dasharray="6,4"/>
                <path d="M540 150 L590 150" stroke-dasharray="6,4"/>
                <path d="M810 150 L860 150" stroke-dasharray="6,4"/>
              </g>

              {/* 装饰元素 */}
              <g fill="#1ED6FF" opacity="0.1">
                <circle cx="50" cy="20" r="8"/>
                <circle cx="1050" cy="280" r="12"/>
                <circle cx="550" cy="30" r="10"/>
              </g>
            </svg>
          </div>

          <div className={styles.examContent}>
            <Paragraph className={styles.paragraph}>
              职称考试是评价专业技术人员职业能力的重要方式，是专业技术人员职业发展的重要途径。通过职称考试，可以获得相应的职称资格，这对个人职业发展具有重要意义。
            </Paragraph>
            
            <Paragraph className={styles.paragraph}>
              本系统提供了全面的备考资源：
              <ul>
                <li>系统化的知识点梳理</li>
                <li>大量真题练习</li>
                <li>专业的答疑解析</li>
                <li>完整的模拟测试</li>
                <li>个性化的学习计划</li>
              </ul>
            </Paragraph>
            
            <Paragraph className={styles.paragraph}>
              通过科学的学习方法和持续的努力，考生可以更好地掌握考试内容，提高通过率。祝愿每位考生都能取得理想的成绩！
            </Paragraph>
          </div>
        </Card>
      </div>
    );
  }

  // 如果是其他子路由，渲染子组件
  if (location.pathname !== '/exam') {
    return <Outlet />;
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <Result
          status="error"
          title="加载失败"
          subTitle="获取分类数据时出错，请稍后重试"
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className={styles.emptyContainer}>
        <Empty description="暂无试题分类" />
      </div>
    );
  }

  return (
    <div className={styles.examSystemContainer}>
      <div className={styles.header}>
        <Title level={2} className={styles.pageTitle}>
          考试系统
        </Title>
        <div className={styles.description}>
          选择试题分类开始学习
        </div>
      </div>
      
      <Row gutter={[24, 24]} className={styles.cardContainer}>
        {categories.map((category) => (
          <Col xs={24} sm={12} md={8} lg={6} key={category.name}>
            <Card 
              hoverable 
              className={styles.categoryCard}
              onClick={() => handleCategoryClick(category)}
            >
              <div className={styles.cardContent}>
                <div className={styles.iconWrapper}>
                  <BookOutlined className={styles.categoryIcon} />
                </div>
                <div className={styles.categoryInfo}>
                  <Title level={4} className={styles.categoryTitle}>
                    {category.name}
                  </Title>
                  <div className={styles.categorySubtitle}>
                    {category.category}
                  </div>
                  <Row gutter={16}>
                    <Col>
                      <Statistic
                        title={<span style={{ color: '#ffffff8c' }}>题目数量</span>}
                        value={category.count}
                        valueStyle={{ color: '#1ED6FF' }}
                      />
                    </Col>
                    <Col>
                      <Statistic
                        title={<span style={{ color: '#ffffff8c' }}>已完成</span>}
                        value={category.completedCount}
                        valueStyle={{ color: '#52c41a' }}
                        prefix={<CheckCircleOutlined />}
                      />
                    </Col>
                  </Row>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default ExamSystem; 